import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {MONTHS} from "../constants"


const ShopLogsChart = ({
  intrusionLogs = [],
  doorWarningLogs = [],
  title = "Security Events",
}) => {
  // ======================================================
  // Filter State
  // Controls selected year and selected month
  // ======================================================
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  /*
   * Controls which event series are visible in the chart.
   */
  const [showIntrusion, setShowIntrusion] = useState(true);
  const [showDoorWarnings, setShowDoorWarnings] = useState(true);

  /*
   * Build the year dropdown using timestamps from both event collections.
   */
  const availableYears = useMemo(() => {
    const allLogs = [...intrusionLogs, ...doorWarningLogs];

    const years = allLogs
      .map((log) => new Date(log.timestamp).getFullYear())
      .filter((year) => !Number.isNaN(year));

    return [...new Set(years)].sort((a, b) => b - a);
  }, [intrusionLogs, doorWarningLogs]);

  /*
   * Filter a log collection using the selected year and month.
   */
  const filterLogsByDate = (logList) => {
    return logList.filter((log) => {
      if (!log.timestamp) return false;

      const date = new Date(log.timestamp);

      if (Number.isNaN(date.getTime())) {
        return false;
      }

      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, "0");

      const matchesYear = selectedYear ? year === selectedYear : true;

      const matchesMonth = selectedMonth ? month === selectedMonth : true;

      return matchesYear && matchesMonth;
    });
  };

  /*
   * Apply the same date filters to Intrusion Alarms.
   */
  const filteredIntrusionLogs = useMemo(() => {
    return filterLogsByDate(intrusionLogs);
  }, [intrusionLogs, selectedYear, selectedMonth]);

  /*
   * Apply the same date filters to Door Open Warnings.
   */
  const filteredDoorWarningLogs = useMemo(() => {
    return filterLogsByDate(doorWarningLogs);
  }, [doorWarningLogs, selectedYear, selectedMonth]);

  /*
   * Group a log collection by month or by day.
   *
   * When a month is selected, logs are grouped by day.
   * Otherwise, logs are grouped by month.
   */
  const groupLogsByDate = (logList) => {
    const groupedData = {};

    logList.forEach((log) => {
      const date = new Date(log.timestamp);

      if (Number.isNaN(date.getTime())) {
        return;
      }

      const key = selectedMonth
        ? date.toISOString().slice(0, 10)
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0",
          )}`;

      const label = selectedMonth
        ? date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : date.toLocaleDateString("en-GB", {
            month: "short",
            year: "numeric",
          });

      if (!groupedData[key]) {
        groupedData[key] = {
          count: 0,
          logs: [],
          label,
        };
      }

      groupedData[key].count += 1;
      groupedData[key].logs.push(log);
    });

    return groupedData;
  };

  /*
   * Build chart categories and both event series.
   *
   * The same date keys are used for both series so that
   * Intrusion Alarms and Door Open Warnings remain aligned.
   */
  const {
    categories,
    intrusionSeriesData,
    doorWarningSeriesData,
    intrusionLogsByDate,
    doorWarningLogsByDate,
  } = useMemo(() => {
    const intrusionGrouped = groupLogsByDate(filteredIntrusionLogs);
    const doorWarningGrouped = groupLogsByDate(filteredDoorWarningLogs);

    /*
     * Combine the date keys from both event collections.
     */
    const allKeys = [
      ...new Set([
        ...Object.keys(intrusionGrouped),
        ...Object.keys(doorWarningGrouped),
      ]),
    ].sort();

    const getLabel = (key) => {
      return (
        intrusionGrouped[key]?.label || doorWarningGrouped[key]?.label || key
      );
    };

    return {
      categories: allKeys.map((key) => getLabel(key)),

      /*
       * Use zero when there are no Intrusion Alarms
       * for a specific date.
       */
      intrusionSeriesData: allKeys.map(
        (key) => intrusionGrouped[key]?.count ?? 0,
      ),

      /*
       * Use zero when there are no Door Open Warnings
       * for a specific date.
       */
      doorWarningSeriesData: allKeys.map(
        (key) => doorWarningGrouped[key]?.count ?? 0,
      ),

      /*
       * Keep the original logs for the custom tooltip.
       */
      intrusionLogsByDate: allKeys.map(
        (key) => intrusionGrouped[key]?.logs ?? [],
      ),

      doorWarningLogsByDate: allKeys.map(
        (key) => doorWarningGrouped[key]?.logs ?? [],
      ),
    };
  }, [filteredIntrusionLogs, filteredDoorWarningLogs, selectedMonth]);

  /*
   * Calculate the total number of Intrusion Alarm events
   * currently included in the selected date range.
   */
  const totalIntrusionLogs = intrusionSeriesData.reduce(
    (sum, value) => sum + value,
    0,
  );

  /*
   * Calculate the total number of Door Open Warning events
   * currently included in the selected date range.
   */
  const totalDoorWarningLogs = doorWarningSeriesData.reduce(
    (sum, value) => sum + value,
    0,
  );

  /*
   * Calculate the total number of visible events.
   *
   * Only enabled chart series are included in this total.
   */
  const totalVisibleLogs =
    (showIntrusion ? totalIntrusionLogs : 0) +
    (showDoorWarnings ? totalDoorWarningLogs : 0);

  /*
   * Build the chart colors based on the currently visible series.
   *
   * Intrusion Alarms use red.
   * Door Open Warnings use amber.
   */
  const chartColors = [];

  const gradientColors = [];

  if (showIntrusion) {
    chartColors.push("#dc2626");
    gradientColors.push("#fca5a5");
  }

  if (showDoorWarnings) {
    chartColors.push("#f59e0b");
    gradientColors.push("#fcd34d");
  }
  // ======================================================
  // ApexCharts Options
  // Controls visual style, tooltip, labels, grid and colors
  // ======================================================
  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "shop-logs-chart",
        type: "area",
        height: 360,
        zoom: { enabled: false },
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 600,
        },
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      },

      dataLabels: {
        enabled: true,
        offsetY: -10,
        style: {
          fontSize: "12px",
          fontWeight: "bold",
          colors: ["#222731"],
        },
        background: {
          enabled: false,
        },
      },

      title: {
        text: title,
        align: "center",
        style: {
          fontSize: "16px",
          fontWeight: 700,
          color: "#44403c",
        },
      },

      subtitle: {
        /*
         * Display the total number of events from
         * the currently visible chart series.
         */
        text: selectedMonth
          ? `${totalVisibleLogs} total events — daily breakdown`
          : `${totalVisibleLogs} total events — monthly breakdown`,
        align: "center",
        style: {
          color: "#78716c",
          fontSize: "12px",
        },
      },

      stroke: {
        curve: "smooth",
        width: 2,
      },

      markers: {
        size: 6,
        hover: { size: 8 },
        strokeWidth: 2,
        radius: 3,

        /*
         * Use a white marker center and match the marker border to each series color.
         */
        colors: chartColors.map(() => "#fafaf9"),
        strokeColors: chartColors,
      },

      grid: {
        borderColor: "#e7e5e4",
        strokeDashArray: 4,
      },

      tooltip: {
        enabled: true,
        shared: false,
        x: { show: true },
        y: {
          formatter: (value) => `${value} ${value > 1 ? "events" : "event"}`,
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const label = w.globals.labels[dataPointIndex] || "";
          const count = series[seriesIndex][dataPointIndex] ?? 0;

          /* Select the correct log collection based on the chart series currently being hovered.*/
          const groupedLogs =
            w.globals.seriesNames[seriesIndex] === "Intrusion Alarms"
              ? intrusionLogsByDate[dataPointIndex] || []
              : doorWarningLogsByDate[dataPointIndex] || [];

          const list = groupedLogs
            .slice(0, 8)
            .map((log) => {
              const date = new Date(log.timestamp);

              const text = selectedMonth
                ? date.toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });

              return `<li style="margin-bottom:4px;">${text}</li>`;
            })
            .join("");

          const more =
            groupedLogs.length > 8
              ? `<div style="font-size:12px; color:#78716c; margin-top:6px;">+ ${
                  groupedLogs.length - 8
                } more</div>`
              : "";

          return `
            <div style="padding:12px; font-family: Inter, sans-serif;  border-radius:8px; max-width:260px;">
    <div style="font-weight:700; color:#44403c; margin-bottom:6px;">${label}</div>

    <div style="font-size:13px; color:#78716c;">
                <strong>${count} ${count > 1 ? "events" : "event"}</strong>
              </div>
               <ul style="margin:8px 0 0 0; padding-left:18px; color:#57534e; font-size:13px;">
                ${list}
              </ul>
              ${more}
            </div>
          `;
        },
      },

      xaxis: {
        categories,
        labels: {
          rotate: selectedMonth ? -45 : 0,
          style: {
            colors: "#78716c",
            fontSize: "12px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },

      yaxis: {
        title: {
          text: "Events",
          style: {
            color: "#a8a29e",
            fontSize: "16px",
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: "#78716c",
          },
        },
        min: 0,
        forceNiceScale: true,
      },

      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.5,
          // gradientToColors: ["#fca5a5"],
          gradientToColors: gradientColors,
          inverseColors: false,
          opacityFrom: 0.45,
          opacityTo: 0.06,
          stops: [0, 80, 100],
        },
      },

      /*
       * Use different colors for each event series. Red represents Intrusion Alarms. Amber represents Door Open Warnings.
       */
      colors: chartColors,
    }),
    [
      categories,
      intrusionLogsByDate,
      doorWarningLogsByDate,
      selectedMonth,
      title,
      totalVisibleLogs,
      showIntrusion,
      showDoorWarnings,
    ],
  );

  // ======================================================
  // Chart Series
  // ApexCharts expects an array of series objects
  // ======================================================
  /*
   * Build the chart series based on the selected event types.
   */
  const series = [];

  if (showIntrusion) {
    series.push({
      name: "Intrusion Alarms",
      data: intrusionSeriesData,
    });
  }

  if (showDoorWarnings) {
    series.push({
      name: "Door Open Warnings",
      data: doorWarningSeriesData,
    });
  }

  return (
    <div className="p-4 bg-stone-50 rounded-2xl shadow-sm border border-stone-100">
      {/* ======================================================
          Header
          Shows chart title, total count and filters
      ====================================================== */}
      <div className="flex items-center justify-between mb-3 gap-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
          {/** Display the total number of events from the currently visible chart series. */}
          <p className="text-sm text-stone-500">
            {selectedMonth
              ? `${totalVisibleLogs} total events — daily breakdown`
              : `${totalVisibleLogs} total events — monthly breakdown`}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/*
           * Allow the user to choose which event series
           * should be displayed in the chart.
           */}
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showIntrusion}
              onChange={(event) => setShowIntrusion(event.target.checked)}
              className="h-4 w-4 accent-red-600"
            />

            <span>Intrusion Alarms</span>
          </label>

          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showDoorWarnings}
              onChange={(event) => setShowDoorWarnings(event.target.checked)}
              className="h-4 w-4 accent-amber-500"
            />

            <span>Door Open Warnings</span>
          </label>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border border-stone-200 bg-stone-50 text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            <option value="">Select Year</option>

            {availableYears.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border border-stone-200 bg-stone-50 text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-stone-300"
          >
            {MONTHS.map((month) => (
              <option key={month.value || "all"} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ======================================================
          Chart Area
          Displays empty state or Apex area chart
      ====================================================== */}
      <div
        id="shop-logs-chart"
        className="rounded-xl bg-gradient-to-b from-stone-50 to-stone-50 p-3"
      >
        {/* Show an empty state when there is no chart data or when all event series are disabled. */}
        {categories.length === 0 || series.length === 0 ? (
          <div className="flex items-center justify-center h-56">
            <div className="text-stone-400">No log data available</div>
          </div>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="area"
            height={360}
          />
        )}
      </div>
    </div>
  );
};

export default ShopLogsChart;
